import React, { useState, useRef, useEffect } from 'react'
import { CircularProgressbar } from "react-circular-progressbar";

const InterviewContainer = ({ isOpen, question,currentQuestionIndex, handleSaveAnswer }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [stream, setStream] = useState(null);
  const [isPlayback, setIsPlayback] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [recordingProgress, setRecordingProgress] = useState(0);
  const [maxRecordingTime] = useState(120); // 2 minutes max recording time
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  
  const videoRef = useRef(null);
  const recordedVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const timerRef = useRef(null);
  const countdownRef = useRef(null);

  useEffect(() => {
    if (isOpen && question && question.get('type_parent') === 'video_question') {
      navigator.mediaDevices.getUserMedia({ video: true, audio: true })
        .then(mediaStream => {
          setStream(mediaStream);
          if (videoRef.current) {
            videoRef.current.srcObject = mediaStream;
          }
        })
        .catch(err => {
          console.error('Camera error:', err);
          alert('Unable to access camera. Please ensure you have granted camera permissions.');
        });
        if (question.get('user_answer')) {
          console.log('LOAD VIDEO', question.get('user_answer'));
          recordedVideoRef.current.src = question.get('user_answer');
          recordedVideoRef.current.load();
          setIsPlayback(true);
          setIsVideoPlaying(false)
        }else{
          console.log('NO VIDEO');
          setIsPlayback(false);
          recordedVideoRef.current.src = '';
          recordedVideoRef.current.load();
          setIsVideoPlaying(false);
        }
    }
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [isOpen, question]);

  const startCountdown = () => {
    setIsCountingDown(true);
    setCountdown(10);
    
    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countdownRef.current);
          setIsCountingDown(false);
          startRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const startRecording = () => {
    if (!stream) return;
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    
    const chunks = [];
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: 'video/mp4' });
      blob.name = 'try_recorded_video.mp4';
      setRecordedBlob(blob);
      setIsPlayback(true);
      
      if (recordedVideoRef.current) {
        const reader = new FileReader();
        reader.onload = function(e) {
          recordedVideoRef.current.src = e.target.result;
          recordedVideoRef.current.load();
          handleSaveAnswer(e, currentQuestionIndex, question.get('id'), 'video', blob);
        };
        reader.readAsDataURL(blob);
      }
    };

    mediaRecorder.start();
    setIsRecording(true);
    setRecordingTime(0);
    setRecordingProgress(0);

    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        const newTime = prev + 1;
        const progress = (newTime / maxRecordingTime) * 100;
        setRecordingProgress(Math.min(progress, 100));
        
        if (newTime >= maxRecordingTime) {
          stopRecording();
        }
        return newTime;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const playRecordedVideo = () => {
    if (recordedVideoRef.current) {
      // Ensure video is properly loaded before playing
      if (recordedVideoRef.current.readyState >= 2) {
        recordedVideoRef.current.play().catch(err => {
          console.error('Error playing video:', err);
        });
        setIsVideoPlaying(true);
      } else {
        // Wait for video to load before playing
        recordedVideoRef.current.addEventListener('canplay', () => {
          recordedVideoRef.current.play().catch(err => {
            console.error('Error playing video:', err);
          });
          setIsVideoPlaying(true);
        }, { once: true });
      }
    }
  };

  const stopRecordedVideo = () => {
    if (recordedVideoRef.current) {
      recordedVideoRef.current.pause();
      recordedVideoRef.current.currentTime = 0;
      setIsVideoPlaying(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="interview-container">
      <div className="body-video vertical-item--center">
        <video
          ref={videoRef}
          className={`video-player ${isPlayback ? 'hide' : ''}`}
          autoPlay
          muted
          playsInline
          style={{ width: '100%', maxHeight: '400px', background: '#000' }}
        />
        <video
          ref={recordedVideoRef}
          className={`video-player ${!isPlayback ? 'hide' : ''}`}
          playsInline
          preload="metadata"
          style={{ width: '100%', maxHeight: '400px', background: '#000' }}
          onPlay={() => setIsVideoPlaying(true)}
          onPause={() => setIsVideoPlaying(false)}
          onEnded={() => setIsVideoPlaying(false)}
        />
        {isCountingDown && (
          <>
            <div className={'alert--light alert--top'}>
              <h5 className="text normal">Recording starts in…</h5>
            </div>
            <div className={'big-counter'}>
              <span className="heading1 text">{countdown}</span>
            </div>
          </>
        )}
      </div>

      {isRecording && (
        <div className="footer-video-progress">
          <span className={``}>
            {formatTime(maxRecordingTime - recordingTime)}
          </span>
          <div
            className={`video-progress text-center`}
            style={{ width: `${recordingProgress}%` }}
          ></div>
        </div>
      )}
      
      <div className="footer-video horizontal-item--center">
        <div 
          style={{ width: "59px", height: "59px" }}
          className={`${isCountingDown && !isRecording && !isPlayback ? 'hide' : ''}`}
        >
            <a 
              className={`btn-start ${isRecording || isCountingDown || isPlayback ? 'hide' : ''}`}
              onClick={startCountdown}
              style={{ cursor: 'pointer' }}
            >
              START
            </a>
            <a 
              className={`btn-stop ${!isRecording ? 'hide' : ''}`}
              onClick={stopRecording}
              style={{ cursor: 'pointer' }}
            >
                <CircularProgressbar
                    className={`circular-counter`}
                    value={recordingProgress}
                    background="#FFFFFF"
                    strokeWidth="6"
                    backgroundPadding="0"
                    text={recordingTime <= 4 ? formatTime(maxRecordingTime - recordingTime) : 'Finish'}
                    counterClockwise={true}
                />
            </a>
            <a 
              className={`btn-play-video ${!isPlayback || isVideoPlaying ? 'hide' : ''}`}
              onClick={playRecordedVideo}
              style={{ cursor: 'pointer' }}
            >
              <i className="fa fa-play"></i>
            </a>
            <a 
              className={`btn-stop-video ${!isPlayback || !isVideoPlaying ? 'hide' : ''}`}
              onClick={stopRecordedVideo}
              style={{ cursor: 'pointer' }}
            >
              <i className="fa fa-stop"></i>
            </a>
        </div>
        {isCountingDown && countdown <= 3 && countdown > 0 && (
          <div style={{ width: "59px", height: "59px" }}>
            <CircularProgressbar
              className={`circular-counter`}
              value={((4 - countdown) / 3) * 100}
              background="#FFFFFF"
              strokeWidth="6"
              backgroundPadding="0"
              text={countdown.toString()}
              counterClockwise={true}
            />
          </div>
        )}
        {isCountingDown && countdown > 3 && (
          <div>
            <a 
              className="btn-start-now"
              onClick={() => {
                clearInterval(countdownRef.current);
                setCountdown(3);
                
                countdownRef.current = setInterval(() => {
                  setCountdown(prev => {
                    if (prev <= 1) {
                      clearInterval(countdownRef.current);
                      setIsCountingDown(false);
                      startRecording();
                      return 0;
                    }
                    return prev - 1;
                  });
                }, 1000);
              }}
              style={{ cursor: 'pointer' }}
            >
              START NOW
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewContainer;
